// src/components/partner/PartnerPriceListSearchPanel.tsx
//
// PartnerPriceListSearchPanel
// - Presentation-only search UI for PartnerPriceList page.
// - Delegates all state ownership to the parent page.
// - Uses the shared SearchPanel container for:
//   - Search / Reset buttons (single source of truth)
//   - Optional auto-search (debounced) controlled by the page via `autoSearch` prop
//   - Active filter chips (removable, with readable labels)
//
// IMPORTANT:
// - Do NOT hardcode autoSearch defaults here.
//   SearchPanel has global defaults (enabled: false, delayMs: 500) via ./constants.
//   This component should only pass through `autoSearch` if provided by the page.

import React, { useMemo } from 'react';
import { Autocomplete, Grid, TextField } from '@mui/material';

import {
  SearchPanel,
  SearchTextField,
  type ActiveChipsConfig,
  type SearchChipSpec,
  type SetSearchValueFn,
} from '@components/common/search';

import type { GetProductsParams, ProductSaleType } from '@apis/product';
import type { ProductStatus } from '@models/product';

type ProductSearchForm = GetProductsParams;

type Option<T> = {
  label: string;
  value: T;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  // Draft search state (owned by page)
  draftSearch: ProductSearchForm;
  setValue: SetSearchValueFn<ProductSearchForm>;

  // Options + selected values (owned by page)
  countryOptions: Option<number>[];
  stockOptions: Option<number>[];
  saleTypeOptions: Option<ProductSaleType>[];
  statusOptions: Option<ProductStatus>[];

  selectedCountry: Option<number> | null;
  selectedStock: Option<number> | null;
  selectedSaleType: Option<ProductSaleType> | null;
  selectedStatus: Option<ProductStatus> | null;

  codeHelperText?: string;
  nameHelperText?: string;

  // Actions (owned by page)
  onSearch: () => void;
  onReset: () => void;

  /**
   * Auto-search configuration provided by the page.
   * If omitted, SearchPanel falls back to its global defaults.
   */
  autoSearch?: { enabled: boolean; delayMs?: number };
};

const PartnerPriceListSearchPanel: React.FC<Props> = ({
  open,
  onOpenChange,

  draftSearch,
  setValue,

  countryOptions,
  stockOptions,
  saleTypeOptions,
  statusOptions,

  selectedCountry,
  selectedStock,
  selectedSaleType,
  selectedStatus,

  codeHelperText = ' ',
  nameHelperText = ' ',

  onSearch,
  onReset,

  autoSearch,
}) => {
  /* ------------------------------------------------------------------ */
  /* Lookup maps for chips (O(1) label access)                           */
  /* ------------------------------------------------------------------ */

  const countryLabelById = useMemo(() => {
    const map = new Map<number, string>();
    countryOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [countryOptions]);

  const stockLabelById = useMemo(() => {
    const map = new Map<number, string>();
    stockOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [stockOptions]);

  const saleTypeLabelByValue = useMemo(() => {
    const map = new Map<ProductSaleType, string>();
    saleTypeOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [saleTypeOptions]);

  const statusLabelByValue = useMemo(() => {
    const map = new Map<ProductStatus, string>();
    statusOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [statusOptions]);

  /* ------------------------------------------------------------------ */
  /* Active chips configuration                                          */
  /* ------------------------------------------------------------------ */

  const chips: SearchChipSpec<ProductSearchForm>[] = useMemo(
    () => [
      {
        key: 'productCode',
        label: 'Product Code',
        getValueText: (s) => String(s.productCode ?? '').trim(),
        clear: ({ setValue }) => setValue('productCode', null),
      },
      {
        key: 'productName',
        label: 'Product Name',
        getValueText: (s) => String(s.productName ?? '').trim(),
        clear: ({ setValue }) => setValue('productName', null),
      },
      {
        key: 'countryId',
        label: 'Country',
        isActive: (s) => typeof s.countryId === 'number',
        getValueText: (s) => {
          const id = s.countryId;
          if (typeof id !== 'number') return '';
          return countryLabelById.get(id) ?? String(id);
        },
        clear: ({ setValue }) => setValue('countryId', null),
      },
      {
        key: 'stockId',
        label: 'Stock',
        isActive: (s) => typeof s.stockId === 'number',
        getValueText: (s) => {
          const id = s.stockId;
          if (typeof id !== 'number') return '';
          return stockLabelById.get(id) ?? String(id);
        },
        clear: ({ setValue }) => setValue('stockId', null),
      },
      {
        key: 'productSaleType',
        label: 'Sale Type',
        isActive: (s) => Boolean(s.productSaleType),
        getValueText: (s) => {
          const v = s.productSaleType as ProductSaleType | undefined;
          if (!v) return '';
          return saleTypeLabelByValue.get(v) ?? String(v);
        },
        clear: ({ setValue }) => setValue('productSaleType', null),
      },
      {
        key: 'productStatus',
        label: 'State',
        isActive: (s) => Boolean(s.productStatus),
        getValueText: (s) => {
          const v = s.productStatus as ProductStatus | undefined;
          if (!v) return '';
          return statusLabelByValue.get(v) ?? String(v);
        },
        clear: ({ setValue }) => setValue('productStatus', null),
      },
    ],
    [countryLabelById, saleTypeLabelByValue, statusLabelByValue, stockLabelById],
  );

  const chipsConfig: ActiveChipsConfig<ProductSearchForm> = useMemo(
    () => ({
      enabled: true,
      search: draftSearch,
      setValue,
      chips,
      maxVisible: 2,
      // Keep behavior consistent with other pages:
      // chip removal should immediately apply search (manual / auto-search is optional).
      onAfterClear: onSearch,
    }),
    [chips, draftSearch, onSearch, setValue],
  );

  // ✅ 지침 강화: enabled=true인 경우에만 패스스루
  const panelAutoSearch = autoSearch?.enabled === true ? autoSearch : undefined;

  return (
    <SearchPanel
      title="Filters"
      open={open}
      onOpenChange={onOpenChange}
      onSearch={onSearch}
      onReset={onReset}
      chipsConfig={chipsConfig}
      // Pass-through only. If undefined, SearchPanel uses global defaults.
      autoSearch={panelAutoSearch}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            fullWidth
            label="Product Code"
            placeholder="Enter product code"
            size="small"
            value={draftSearch.productCode ?? ''}
            onChangeValue={(v) => setValue('productCode', v)}
            onClear={() => setValue('productCode', null)}
            helperText={codeHelperText}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            fullWidth
            label="Product Name"
            placeholder="Enter product name"
            size="small"
            value={draftSearch.productName ?? ''}
            onChangeValue={(v) => setValue('productName', v)}
            onClear={() => setValue('productName', null)}
            helperText={nameHelperText}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<number>, false, false, false>
            disablePortal
            options={countryOptions}
            value={selectedCountry}
            onChange={(_e, v) => setValue('countryId', (v?.value ?? null) as number | null)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <TextField {...params} label="Country" placeholder="Select country" size="small" helperText=" " />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<ProductSaleType>, false, false, false>
            disablePortal
            options={saleTypeOptions}
            value={selectedSaleType}
            onChange={(_e, v) => setValue('productSaleType', (v?.value ?? null) as ProductSaleType | null)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <TextField {...params} label="Sale Type" placeholder="Select sale type" size="small" helperText=" " />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<number>, false, false, false>
            disablePortal
            options={stockOptions}
            value={selectedStock}
            onChange={(_e, v) => setValue('stockId', (v?.value ?? null) as number | null)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <TextField {...params} label="Stock Code" placeholder="Select stock" size="small" helperText=" " />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<ProductStatus>, false, false, false>
            disablePortal
            options={statusOptions}
            value={selectedStatus}
            onChange={(_e, v) => setValue('productStatus', (v?.value ?? null) as ProductStatus | null)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <TextField {...params} label="State" placeholder="Select state" size="small" helperText=" " />
            )}
          />
        </Grid>
      </Grid>
    </SearchPanel>
  );
};

export default PartnerPriceListSearchPanel;
